import React, { useEffect, useRef, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { ArrowUpIcon } from "@heroicons/react/24/outline";
import { Button } from "primereact/button";
import logosmall from "../assets/images/logosmall.png";
import apiFunctions from "../services/api-service";
import HomeContentView from "../components/homeBuilder/HomeContentView";
import { applySeoTags } from "../utils/seoMeta";

// Public renderer for extra builder-made pages (About Us, Contact, ...) at
// /p/<slug>. Same pipeline as the homepage (HomeContentView, which wraps its
// own ResponsiveScaler internally), with the page's own sections/background
// and the shared
// site-wide header/footer that comes along in the same API response.
const PublicSitePage: React.FC = () => {
    const { slug } = useParams();
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [content, setContent] = useState<any>(null);
    const [liveVideos, setLiveVideos] = useState<any[]>([]);
    const [pageVideos, setPageVideos] = useState<any[]>([]);
    const [events, setEvents] = useState<any[]>([]);
    // Restores the base index.html SEO tags on unmount/slug-change — this is
    // a single SPA, so without this, one page's tags would keep showing
    // after client-side navigation away from it.
    const seoCleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        const handleScroll = () => setShowScrollToTop(window.scrollY > 300);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        apiFunctions.get(`/sitepage/public/${slug}`).then((res) => {
            if (res?.data?.success && res.data.data?.page) {
                const { page, header, footer } = res.data.data;
                setContent({
                    sections: page.sections || [],
                    pagebg: page.pagebg || "",
                    showinstallbanner: page.showinstallbanner || false,
                    header: header || {},
                    footer: footer || {},
                });
                const canonicalUrl = `https://sagarsamaj.in/p/${page.slug}`;
                seoCleanupRef.current = applySeoTags({
                    title: page.metatitle || (page.title ? `${page.title} | Sagar Samaj` : undefined),
                    description: page.metadescription || undefined,
                    keywords: page.metakeywords || undefined,
                    canonicalUrl,
                    jsonLd: {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        name: page.title,
                        description: page.metadescription || undefined,
                        url: canonicalUrl,
                        isPartOf: { "@type": "WebSite", name: "Sagar Samaj", url: "https://sagarsamaj.in/" },
                    },
                });
            } else {
                setNotFound(true);
            }
        }).catch(() => setNotFound(true)).finally(() => setLoading(false));

        apiFunctions.get("/livevideo/webapp/list").then((res) => {
            if (res?.data?.success) setLiveVideos(res.data.data || []);
        }).catch(() => { });
        apiFunctions.get("/pagevideo/webapp/list").then((res) => {
            if (res?.data?.success) setPageVideos(res.data.data || []);
        }).catch(() => { });
        apiFunctions.get("/event/webapp/list").then((res) => {
            if (res?.data?.success) setEvents(res.data.data || []);
        }).catch(() => { });

        return () => {
            seoCleanupRef.current?.();
            seoCleanupRef.current = null;
        };
    }, [slug]);

    if (loading) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-white z-50">
                <img src={logosmall} alt="Sagar Samaj" className="w-20 h-20" />
                <span className="text-xl font-semibold" style={{ color: "#264164" }}>Sagar Samaj</span>
            </div>
        );
    }

    // Missing/hidden (isactive:false) page — bounce straight to the homepage
    // instead of showing a "Page not found" screen, same as the app-wide
    // catch-all route (see routes/Routes.tsx) for any other unreachable path.
    if (notFound) {
        return <Navigate to="/" replace />;
    }

    return (
        <div>
            <HomeContentView
                homeContent={content}
                liveVideos={liveVideos}
                pageVideos={pageVideos}
                events={events}
                logo={logosmall}
                sticky
            />
            {showScrollToTop && (
                <Button
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="fixed bottom-5 right-5 bg-blue-500 text-white p-3 rounded-full shadow-lg hover:bg-blue-600 transition duration-300"
                    style={{ zIndex: 1000 }}
                >
                    <ArrowUpIcon style={{ width: 24, height: 24 }} />
                </Button>
            )}
        </div>
    );
};

export default PublicSitePage;
